import React, { useMemo, useState, useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as yup from 'yup'

import useTranslation from 'hooks/useTranslations'

import AddVendor from 'ui/modules/addVendor'

import {
  addVendor,
  addVendorClear,
  confirmAddVendorFake,
} from 'services/vendors/actions'
import {
  getIsNotRegistered,
  getIsAddVendorSucceeded,
  getIsAddVendorConfirmed,
  getAddVendorError,
} from 'services/vendors/selectors'
import { getSearchConfig } from 'services/config/selectors'
import * as actionTypesVendors from 'services/vendors/action-types'
import { showSnackbar } from 'services/snackbar/actions'
import { getCountries } from 'services/config/selectors'

import reduceArrayToObject from 'utils/reduceArrayToObject'
import validateVAT from 'utils/validateVAT'
import validatePhone from 'utils/validatePhone'
import { loadLocale } from 'services/locale'
import { getProcurementInfo } from 'services/procurement/actions'
import { getProcurementVendorTypesInformation } from 'services/procurement/selectors'

const AddVendorModal = ({ isOpen, onClose }) => {
  const t = useTranslation()
  const dispatch = useDispatch()
  const isConfirmed = useSelector(getIsAddVendorConfirmed)
  const isSucceeded = useSelector(getIsAddVendorSucceeded)
  const notRegistered = useSelector(getIsNotRegistered)
  const serverError = useSelector(getAddVendorError)
  const countries = useSelector(getCountries)
  const searchConfig = useSelector(getSearchConfig)
  const [cifError, setCifError] = useState('')
  const [emailError, setEmailError] = useState('')
  const [phoneError, setPhoneError] = useState('')
  const procumerementVendorTypes = useSelector(
    getProcurementVendorTypesInformation,
  )

  const locale = loadLocale() || 'es'
  const validationSchema = useMemo(() => {
    return yup.object().shape({
      email: yup.string().email({ emailError: t('error:invalid-email') }),
      identification_code: yup
        .string()
        .test('valid-cif', { cifError: t('error:invalid-cif') }, function (
          cif,
        ) {
          const country = this.parent.country_code
          return validateVAT(cif, country)
        }),
      phone: yup
        .string()
        .test(
          'valid-phone',
          { phoneError: t('error:invalid-phone') },
          function (phone) {
            return validatePhone(phone)
          },
        ),
    })
  }, [t])

  const validateForm = useCallback(
    data =>
      validationSchema.validate(data, { abortEarly: false }).catch(error => {
        const errors = reduceArrayToObject(error.errors)
        throw errors
      }),
    [validationSchema],
  )

  const handleSubmit = async data => {
    const payload = { identification_code: data.cif }
    setEmailError('')
    setCifError('')
    setPhoneError('')

    if (data.vendorTypes && 0 < data.vendorTypes.length) {
      payload.vendor_types = data.vendorTypes.map(vendorType => vendorType.id)
    }

    if (notRegistered) {
      payload.country_code = data.country
      payload.email = data.email
      payload.name = data.companyName
      payload.phone = data.phone

      validateForm(payload)
        .then(payload => {
          dispatch(confirmAddVendorFake())
          dispatch(
            showSnackbar({
              message: 'vendors:invitation-send',
              actionToDo: {
                type: actionTypesVendors.ADD_VENDOR,
                payload,
              },
            }),
          )
        })
        .catch(errors => {
          setEmailError(errors.emailError || '')
          setCifError(errors.cifError || '')
          setPhoneError(errors.phoneError || '')
        })
    } else {
      dispatch(addVendor(payload))
    }
  }

  useEffect(() => {
    if (isOpen && 0 < searchConfig.vendorTypes.length) {
      dispatch(getProcurementInfo(locale))
    }
  }, [dispatch, locale, searchConfig.vendorTypes, isOpen])

  useEffect(() => {
    isSucceeded &&
      dispatch(showSnackbar({ message: 'vendors:invitation-send' }))
  }, [dispatch, isSucceeded, t])

  const handleCloseModal = () => {
    onClose()
    dispatch(addVendorClear())
  }

  return (
    (0 === searchConfig.vendorTypes.length ||
      0 < procumerementVendorTypes.length) && (
      <AddVendor
        isOpen={isOpen}
        isDisabled={false}
        showNewVendor={notRegistered}
        isConfirmed={isConfirmed}
        cifError={cifError}
        emailError={emailError}
        phoneError={phoneError}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        countries={countries}
        vendorTypesOptions={procumerementVendorTypes}
        genericError={serverError && serverError.generic}
      />
    )
  )
}

export default AddVendorModal
